import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Scale, Award, HeartHandshake, ChevronRight, MapPin, PhoneCall, ClipboardList } from "lucide-react";
import { COLLECTIONS, SERVICE_AREAS, HERO_INTERIOR, POSTERS } from "@/lib/collections";
import { VIDEOS } from "@/lib/videos";
import { KgdLogo } from "@/components/Layout";
import GoogleReviews from "@/components/GoogleReviews";
import EvaluationSection from "@/components/EvaluationSection";
import StandardKGD, { BrandSignature } from "@/components/StandardKGD";
import Composer from "@/components/Composer";
import { HeroVideoBackground } from "@/components/VideoBlock";

const VALUES = [
    { icon: ShieldCheck, label: "Confiance", desc: "Une équipe fiable, professionnelle et discrète." },
    { icon: Scale, label: "Rigueur", desc: "Une méthode structurée à chaque intervention." },
    { icon: Award, label: "Excellence", desc: "Un souci du détail digne de la joaillerie." },
    { icon: HeartHandshake, label: "Tranquillité d'esprit", desc: "Un espace apaisant qui vous ressemble." },
];

function FacetLines() {
    return (
        <svg viewBox="0 0 1600 600" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-[0.14] pointer-events-none" aria-hidden="true">
            <defs>
                <linearGradient id="fx1" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity="0"/>
                    <stop offset="50%" stopColor="#E8D48B"/>
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity="0"/>
                </linearGradient>
            </defs>
            <path d="M0,320 C420,120 900,520 1600,220" stroke="url(#fx1)" strokeWidth="0.6" fill="none"/>
            <path d="M0,220 C520,420 1080,120 1600,360" stroke="url(#fx1)" strokeWidth="0.5" fill="none"/>
            <path d="M0,420 C500,220 1000,540 1600,80" stroke="url(#fx1)" strokeWidth="0.4" fill="none"/>
        </svg>
    );
}

function CollectionCard({ c, idx }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: idx * 0.06, ease: [0.2,0.7,0.2,1] }}
            data-testid={`coll-card-${c.slug}`}
            className="group"
        >
            <Link to={`/collections/${c.slug}`} className="block luxe-card overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-24px_rgba(4,17,38,0.28)]" style={{ borderColor: `${c.border}55` }}>
                <div className="card-square relative">
                    <img src={c.poster} alt={`Collection ${c.name} — ${c.subtitle}`} loading="lazy" className="transition-transform duration-[900ms] group-hover:scale-[1.03]"/>
                </div>
                <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-kgd-gold/15">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="eyebrow">Collection {c.name}</div>
                            <div className="text-sm sm:text-base text-[#041126]/85 mt-0.5">{c.subtitle}</div>
                        </div>
                        <span data-testid={`coll-card-cta-${c.slug}`} className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap pt-0.5" style={{ color: c.primary }}>
                            Découvrir <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1"/>
                        </span>
                    </div>
                    {c.emotional && <p className="mt-3 text-[13px] italic text-slate-500 leading-snug">« {c.emotional} »</p>}
                    {c.investment && (
                        <div className="mt-3 pt-3 border-t border-dashed border-kgd-gold/25 flex items-baseline gap-2">
                            <span className="text-[10px] uppercase tracking-widest text-slate-500">Investissement</span>
                            <span className="text-[13px] font-medium text-[#041126]">{c.investment}</span>
                        </div>
                    )}
                </div>
            </Link>
        </motion.div>
    );
}

const QUIZ = [
    { q: "Est-ce pour un espace résidentiel ou commercial?", options: [
        { label: "Résidentiel", value: "res" }, { label: "Commercial", value: "com" }
    ]},
    { q: "Quel type de service cherchez-vous?", options: [
        { label: "Entretien régulier", value: "reg" },
        { label: "Grand ménage / après-chantier", value: "grand" },
        { label: "Services à la carte", value: "carte" },
        { label: "Service premium personnalisé", value: "premium" },
    ]},
    { q: "Quelle fréquence envisagez-vous?", options: [
        { label: "Hebdomadaire", value: "week" }, { label: "Aux 2 semaines", value: "biweek" },
        { label: "Mensuel", value: "month" }, { label: "Occasionnel", value: "once" },
    ]},
    { q: "Comment décririez-vous actuellement votre espace?", options: [
        { label: "Déjà bien entretenu", value: "bien" },
        { label: "Besoin d'un entretien régulier", value: "reg" },
        { label: "Besoin d'une remise à niveau", value: "raniveau" },
        { label: "Très exigeant / après travaux", value: "chantier" },
        { label: "Je préfère qu'on l'évalue", value: "evalue" },
    ]},
    { q: "Souhaitez-vous ajouter des attentions Perle?", options: [
        { label: "Four",           value: "four" },
        { label: "Réfrigérateur",  value: "frigo" },
        { label: "Fenêtres",       value: "fenetres" },
        { label: "Sous-sol",       value: "sous-sol" },
        { label: "Organisation",   value: "pliage" },
        { label: "Aucune option pour l'instant", value: "aucune" },
    ]},
];

function recommend(answers) {
    if (answers[0] === "com") return "saphir";
    if (answers[3] === "chantier" || answers[3] === "raniveau") return "rubis";
    if (answers[1] === "grand") return "rubis";
    if (answers[1] === "carte") return "perle";
    if (answers[1] === "premium") return "diamant";
    return "emeraude";
}

function Quiz() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [result, setResult] = useState(null);
    const total = QUIZ.length;

    const select = (val) => {
        const next = [...answers, val];
        setAnswers(next);
        if (step + 1 >= total) setResult(recommend(next));
        else setStep(step + 1);
    };
    const reset = () => { setStep(0); setAnswers([]); setResult(null); };
    const rec = result ? COLLECTIONS.find(c => c.slug === result) : null;
    const perleOpt = answers[4];
    const perleQS = (perleOpt && perleOpt !== "aucune") ? `&option=${perleOpt}` : "";

    return (
        <section className="relative py-20 lg:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg, #041126 0%, #071A33 60%, #0A2555 100%)" }} data-theme="dark">
            <FacetLines/>
            <div className="max-w-3xl mx-auto px-5 lg:px-8 relative">
                <div className="text-center mb-10">
                    <div className="eyebrow">Trouver ma Collection</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-3 curve-divider">Quelle Collection vous correspond?</h2>
                    <p className="text-white/70 mt-8 text-sm sm:text-base">Cinq questions rapides pour identifier la Collection Ménage KGD la plus adaptée à vos besoins.</p>
                </div>

                <div className="glass-card rounded-2xl p-6 sm:p-10">
                    {!result ? (
                        <>
                            <div className="flex items-center justify-between text-xs text-white/60 mb-6">
                                <span data-testid="quiz-progress-label">Étape {step + 1} sur {total}</span>
                                <div className="flex gap-1">
                                    {QUIZ.map((_, i) => (
                                        <span key={i} className="h-1 w-6 rounded-full" style={{
                                            background: i <= step
                                                ? "linear-gradient(90deg,#008CFF,#00B8FF 55%,#39D600)"
                                                : "rgba(255,255,255,0.15)"
                                        }}/>
                                    ))}
                                </div>
                            </div>
                            <h3 className="font-serif text-xl sm:text-2xl text-white mb-6" data-testid="quiz-question">{QUIZ[step].q}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {QUIZ[step].options.map((opt) => (
                                    <button key={opt.value} onClick={() => select(opt.value)} data-testid={`quiz-opt-${step}-${opt.value}`}
                                        className="text-left rounded-xl border border-white/15 hover:border-transparent hover:[background:linear-gradient(#0b1a34,#0b1a34)_padding-box,linear-gradient(90deg,#008CFF,#39D600)_border-box] px-5 py-4 text-white/90 hover:text-white transition-all hover:bg-white/5">
                                        <span className="font-medium">{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center" data-testid="quiz-result">
                            <div className="eyebrow" style={{ background: `linear-gradient(90deg, ${rec.primary}, #E8D48B)`, WebkitBackgroundClip: "text", backgroundClip: "text" }}>Votre Collection recommandée</div>
                            <h3 className="font-serif text-3xl sm:text-4xl text-white mt-3">Collection {rec.name}</h3>
                            {rec.emotional && <p className="mt-2 font-serif italic" style={{ color: "#E8D48B" }}>« {rec.emotional} »</p>}
                            <p className="text-white/75 mt-3 max-w-xl mx-auto text-sm sm:text-base">Elle semble correspondre à vos besoins actuels — {rec.subtitle.toLowerCase()}.</p>
                            {perleQS && (
                                <p className="mt-3 text-xs sm:text-sm" style={{ color: "#CBD5E1" }}>Nous ajouterons également votre attention Perle à votre demande.</p>
                            )}
                            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                                <Link to={`/collections/${rec.slug}`} className="btn-ghost-gold" data-testid="quiz-cta-collection">Découvrir cette Collection</Link>
                                <Link to={`/soumission?collection=${rec.slug}${perleQS}`} className="btn-kgd" data-testid="quiz-cta-soumission">Planifier mon évaluation gratuite</Link>
                            </div>
                            <button onClick={reset} className="mt-6 text-xs uppercase tracking-widest text-white/50 hover:text-white" data-testid="quiz-reset">Recommencer</button>
                        </div>
                    )}
                </div>
                <p className="text-center text-xs text-white/50 mt-6 italic">Cet outil ne fournit pas de prix. Chaque soumission est personnalisée selon vos besoins réels.</p>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <div className="bg-[#F8F5EE]">
            {/* HERO */}
            <section className="relative overflow-hidden" style={{ background: "linear-gradient(160deg,#03102A 0%,#041126 45%,#07204A 100%)" }} data-theme="dark">
                <HeroVideoBackground src={VIDEOS.hero.src} poster={VIDEOS.hero.poster || HERO_INTERIOR}/>
                {/* Reinforced global overlay — guarantees night-blue premium mood even when the video shows very bright frames or when Safari serves the poster */}
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: "linear-gradient(180deg, rgba(3,16,42,0.55) 0%, rgba(3,16,42,0.35) 40%, rgba(3,16,42,0.62) 100%)" }}/>
                <FacetLines/>
                <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(7,91,200,0.28), transparent 65%)" }}/>
                <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.16), transparent 65%)" }}/>

                <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-12 sm:pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center" data-theme="dark">
                    <div>
                        <div className="eyebrow lux-in">Ménage KGD</div>
                        <h1 className="font-serif text-[2.25rem] leading-[1.08] sm:text-5xl lg:text-6xl text-white mt-4 lux-in-2" data-testid="hero-title">
                            <span className="inline">Votre espace mérite</span>{" "}
                            <br className="hidden sm:block" aria-hidden="true"/>
                            <span className="inline" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37,#B8912B)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>plus qu'un simple entretien.</span>
                        </h1>
                        <p className="mt-6 text-[15px] sm:text-lg max-w-xl leading-relaxed lux-in-3" style={{ color: "#E8EEF7", opacity: 0.95 }}>
                            Des Collections d'entretien résidentiel et commercial pensées pour offrir rigueur, constance et tranquillité d'esprit.
                        </p>
                        <div className="mt-5 lux-in-3">
                            <BrandSignature/>
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 lux-in-3">
                            <a href="#composer" className="btn-kgd" data-testid="hero-cta-composer" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="composer-section"]')?.scrollIntoView({behavior:'smooth', block:'start'}); }}><Sparkles className="w-4 h-4"/> Trouver ma Collection</a>
                            <Link to="/soumission" className="btn-ghost-gold" data-testid="hero-cta-soumission">Planifier mon évaluation gratuite</Link>
                        </div>
                        <div className="mt-4 lux-in-3 space-y-1 text-center sm:text-left" data-testid="hero-info-lines">
                            <p className="text-[11px] sm:text-xs tracking-wide" style={{ color: "#E8EEF7", opacity: 0.9 }} data-testid="hero-services-line">
                                Commercial • Résidentiel • Après-construction
                            </p>
                            <p className="text-[11px] sm:text-xs tracking-wide" style={{ color: "#CBD5E1", opacity: 0.78 }} data-testid="hero-zones-line">
                                Trois-Rivières • Shawinigan • Bécancour et les environs
                            </p>
                        </div>
                        <div className="mt-10 flex items-center gap-3 lux-in-3">
                            <span className="hidden sm:block h-px w-10" style={{ background: "linear-gradient(90deg,transparent,#D4AF37)" }}/>
                            <p className="font-serif italic text-[#E8D48B] text-base sm:text-lg">« De catastrophique à éclatant, avec constance et rigueur. »</p>
                        </div>
                    </div>
                    <div className="hidden lg:flex justify-end">
                        <div className="relative w-[380px] max-w-full">
                            <div className="absolute -inset-8 rounded-full blur-3xl" style={{ background: "conic-gradient(from 180deg, rgba(0,140,255,0.30), rgba(0,184,255,0.24), rgba(57,214,0,0.24), rgba(212,175,55,0.18), rgba(0,140,255,0.30))" }}/>
                            <div className="relative rounded-2xl border border-kgd-gold/30 shadow-[0_28px_70px_-20px_rgba(0,0,0,0.7)] bg-[#020713] p-10 flex items-center justify-center">
                                <KgdLogo className="h-40"/>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST BAR — ivory panel */}
            <section className="bg-[#FBF8F1] border-y border-kgd-gold/20">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 sm:py-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                    {VALUES.map((v) => (
                        <div key={v.label} data-testid={`value-${v.label.toLowerCase()}`} className="flex flex-col sm:flex-row items-center sm:items-start gap-3 text-center sm:text-left">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 shadow-[0_6px_18px_-6px_rgba(212,175,55,0.55),inset_0_1px_0_rgba(255,255,255,0.5)]" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)" }}>
                                <v.icon className="w-5 h-5 text-[#041126]" strokeWidth={1.75}/>
                            </div>
                            <div>
                                <div className="font-serif text-lg text-[#041126]">{v.label}</div>
                                <div className="text-[12px] sm:text-xs text-slate-600 mt-1 leading-relaxed">{v.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* GOOGLE REVIEWS */}
            <GoogleReviews/>

            {/* LE STANDARD KGD — signature de marque */}
            <StandardKGD/>

            {/* COLLECTIONS INTRO */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 lg:pt-28 pb-6">
                <div className="text-center">
                    <div className="eyebrow">Découvrez nos Collections</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3 curve-divider">Trouvez la Collection qui vous ressemble</h2>
                    <p className="text-slate-600 mt-8 max-w-2xl mx-auto leading-relaxed">Chaque espace est différent. Chaque besoin mérite une attention adaptée. Découvrez la Collection Ménage KGD conçue pour votre quotidien — et son niveau d'investissement.</p>
                </div>
            </section>

            {/* NOS COLLECTIONS — full-width ensemble poster (only once) */}
            <section className="max-w-4xl mx-auto px-5 lg:px-8 pt-8 pb-14">
                <div className="rounded-2xl overflow-hidden bg-[#03102A] shadow-[0_30px_60px_-24px_rgba(4,17,38,0.35)] border border-kgd-gold/20">
                    <div className="card-contain">
                        <img src={POSTERS.ensemble} alt="Aperçu des cinq collections Ménage KGD" loading="lazy"/>
                    </div>
                </div>
            </section>

            {/* CARDS */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 lg:pb-28">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
                    {COLLECTIONS.map((c, i) => <CollectionCard c={c} idx={i} key={c.slug}/>)}
                </div>
            </section>

            {/* DERRIÈRE MÉNAGE KGD — human & institutional presentation */}
            <section className="relative overflow-hidden py-20 lg:py-24" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F4EFE1 100%)" }} data-testid="behind-kgd-section">
                <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.55), transparent)" }}/>
                <div className="absolute inset-x-0 bottom-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.35), transparent)" }}/>
                <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
                    <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16 items-center">
                        {/* Portrait card — real photo of co-owners, framed with subtle gold border */}
                        <div>
                            <figure className="relative mx-auto max-w-sm lg:max-w-none">
                                <div className="absolute -inset-3 rounded-[26px] pointer-events-none" style={{ background: "conic-gradient(from 180deg, rgba(212,175,55,0.16), rgba(232,212,139,0.08), rgba(212,175,55,0.16))", filter: "blur(20px)" }} aria-hidden="true"/>
                                <div className="relative rounded-[20px] overflow-hidden shadow-[0_30px_60px_-24px_rgba(4,17,38,0.35)]" style={{ background: "#03102A", border: "1px solid rgba(212,175,55,0.35)" }}>
                                    <div style={{ aspectRatio: "3/4" }} className="relative">
                                        <img
                                            src="/images/founders.webp"
                                            alt="Katarina et Madorant — cofondateurs de Ménage KGD"
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover"
                                            data-testid="behind-kgd-founders-photo"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: "linear-gradient(0deg, rgba(3,16,42,0.55), transparent)" }}/>
                                        <figcaption className="absolute left-4 right-4 bottom-3 text-white/90 text-[11px] uppercase tracking-[0.18em] font-medium">
                                            Katarina &amp; Madorant · Cofondateurs
                                        </figcaption>
                                    </div>
                                </div>
                            </figure>
                        </div>

                        {/* Copy */}
                        <div>
                            <div className="eyebrow">Derrière Ménage KGD</div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] text-[#041126] mt-3 leading-[1.15]" data-testid="behind-kgd-title">
                                Plus qu'un service d'entretien,<br className="hidden sm:block"/>
                                <span style={{ background: "linear-gradient(90deg,#B8912B,#D4AF37,#E8D48B)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}> une façon de prendre soin de vos espaces.</span>
                            </h2>
                            <div className="mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,#D4AF37,transparent)" }}/>

                            <div className="mt-6 space-y-4 text-slate-700 text-[15px] sm:text-base leading-relaxed">
                                <p>
                                    Ménage KGD est une entreprise fondée par <span className="text-[#041126] font-medium">Katarina</span> et <span className="text-[#041126] font-medium">Madorant</span>, avec une idée simple : offrir un service d'entretien où la <em>rigueur</em>, la <em>constance</em> et la <em>tranquillité d'esprit</em> passent avant tout.
                                </p>
                                <p>
                                    Nous croyons qu'un espace propre ne devrait pas seulement paraître propre. Il devrait être agréable à retrouver, rassurant et entretenu avec attention, visite après visite.
                                </p>
                                <p>
                                    Du résidentiel aux environnements commerciaux, notre approche repose sur une évaluation des besoins, un service adapté et un contrôle de qualité constant.
                                </p>
                            </div>

                            {/* Discreet brand signature */}
                            <div className="mt-7 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#7A6320]" data-testid="behind-kgd-signature">
                                <span>Rigueur</span>
                                <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                                <span>Constance</span>
                                <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                                <span>Tranquillité d'esprit</span>
                            </div>

                            <div className="mt-8">
                                <Link to="/soumission" className="btn-kgd" data-testid="behind-kgd-cta"><Sparkles className="w-4 h-4"/> Demander une évaluation</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Quiz/>

            {/* COMPOSER — Ma sélection KGD */}
            <div id="composer"><Composer/></div>

            {/* NEW — Free evaluation approach */}
            <EvaluationSection/>

            {/* WHY US */}
            <section className="bg-[#FBF8F1] py-20 lg:py-28 border-y border-kgd-gold/15">
                <div className="max-w-6xl mx-auto px-5 lg:px-8">
                    <div className="text-center mb-14">
                        <div className="eyebrow">Pourquoi choisir Ménage KGD?</div>
                        <h2 className="font-serif text-3xl sm:text-4xl text-[#041126] mt-3 curve-divider">Un service pensé comme une pièce de joaillerie</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        {[
                            ["Une approche personnalisée", "Chaque espace est unique — nous adaptons nos gestes et nos produits à votre réalité."],
                            ["Une méthode rigoureuse", "Des étapes structurées et vérifiées pour un résultat constant, visite après visite."],
                            ["Une qualité constante", "La même exigence à chaque intervention, sans compromis sur les détails."],
                            ["Résidentiel et commercial", "Nous accompagnons les particuliers comme les entreprises de la région."],
                            ["Communication professionnelle", "Un contact courtois, ponctuel et transparent, dès la première demande."],
                            ["Tranquillité d'esprit", "Une équipe fiable, discrète et respectueuse de votre espace."],
                        ].map(([t, d], i) => (
                            <div key={t} data-testid={`why-${i}`} className="luxe-card px-5 py-5 sm:px-6 sm:py-6 hover:shadow-[0_20px_40px_-24px_rgba(4,17,38,0.28)] transition-all">
                                <div className="flex items-start gap-3">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                                    <div>
                                        <div className="font-serif text-lg sm:text-xl text-[#041126]">{t}</div>
                                        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative py-20 lg:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg,#03102A,#071A33 60%,#0A2555)" }} data-theme="dark">
                <FacetLines/>
                <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
                    <div className="text-center mb-14">
                        <div className="eyebrow">Comment ça fonctionne</div>
                        <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 curve-divider">Trois étapes vers un espace apaisant</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                        {[
                            { icon: ClipboardList, t: "Parlez-nous de votre espace", d: "Complétez le formulaire ou appelez-nous pour partager vos besoins." },
                            { icon: PhoneCall, t: "Recevez une soumission personnalisée", d: "Nous évaluons soigneusement et vous transmettons une proposition claire." },
                            { icon: Sparkles, t: "Profitez d'un environnement propre", d: "Notre équipe s'occupe de tout, avec constance et rigueur." },
                        ].map((s, i) => (
                            <div key={s.t} data-testid={`step-${i}`} className="glass-card rounded-2xl p-5 sm:p-7 relative flex sm:block gap-4 items-start">
                                <div className="w-10 h-10 rounded-full font-serif flex items-center justify-center text-[#041126] flex-shrink-0 shadow-[0_6px_18px_-6px_rgba(212,175,55,0.6),inset_0_1px_0_rgba(255,255,255,0.4)]" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)" }}>{i + 1}</div>
                                <div>
                                    <s.icon className="hidden sm:block w-5 h-5 text-kgd-gold mt-4"/>
                                    <div className="font-serif text-lg sm:text-xl text-white sm:mt-3">{s.t}</div>
                                    <p className="text-sm text-white/70 mt-1.5 leading-relaxed">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ZONES */}
            <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow">Zones desservies</div>
                        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#041126] mt-3 leading-tight">Trois-Rivières, Shawinigan, Bécancour, Nicolet et les environs</h2>
                        <p className="text-slate-600 mt-4 leading-relaxed">Un service local, humain et attentif, présent dans toute la Mauricie et les municipalités environnantes.</p>
                        <Link to="/zones" className="btn-ghost-gold mt-6 text-[#041126] border-[#041126]/25" data-testid="home-zones-cta">Voir toutes les zones</Link>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SERVICE_AREAS.map(z => (
                            <span key={z} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-kgd-gold/45 text-sm text-[#041126] bg-[#FBF8F1]">
                                <MapPin className="w-3.5 h-3.5 text-kgd-gold"/> {z}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative py-20 lg:py-24 overflow-hidden" style={{ background: "linear-gradient(135deg,#03102A 0%,#041126 45%,#07204A 100%)" }} data-theme="dark">
                <FacetLines/>
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(7,91,200,0.25), transparent 60%)" }}/>
                <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
                    <KgdLogo className="h-20 mx-auto"/>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-6 leading-tight">Votre espace. Vos besoins. Notre Standard.</h2>
                    <div className="mx-auto mt-6 h-px w-24" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="text-white/70 mt-6 max-w-2xl mx-auto leading-relaxed">Planifiez une évaluation gratuite afin que nous puissions comprendre votre espace et vous proposer une solution adaptée à vos besoins.</p>
                    <div className="mt-6 flex justify-center"><BrandSignature/></div>
                    <Link to="/soumission" className="btn-kgd mt-8" data-testid="home-final-cta"><Sparkles className="w-4 h-4"/> Planifier mon évaluation gratuite</Link>
                    <p className="mt-3 text-xs sm:text-sm" style={{ color: "#CBD5E1", opacity: 0.85 }}>
                        Évaluation gratuite • Sans engagement • Proposition adaptée
                    </p>
                </div>
            </section>
        </div>
    );
}
